import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, FilterSettingsModel, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import * as moment from 'moment-timezone';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { SessionService } from '../../../../../../@core/utils/session.service';
import { Order } from '../../../../../../customer-integrations/hollingbery/modules/financial/orders/models/order';
import { OrdersService } from '../../../../../../customer-integrations/hollingbery/modules/financial/orders/services/orders.service';
import { InventoryLotDTO } from '../../../models/inventory-lot';

/** Web-store orders with a line that selected this lot. */
@Component({
  selector: 'lot-order-summary',
  templateUrl: './lot-order-summary.component.html',
  styleUrls: ['./lot-order-summary.component.scss']
})
export class LotOrderSummaryComponent implements OnInit {
  @Input() lot: InventoryLotDTO = {};

  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;

  public faChevronRight = faChevronRight;
  public isLoading = true;
  public allOrders: Order[] = [];
  public orders: Order[] = [];
  public columns: ColumnModel[] = [];

  public pageSettings: PageSettingsModel = { pageSize: 10, pageSizes: [10, 25, 50] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };

  private destroyRef = inject(DestroyRef);

  constructor(
    private orderService: OrdersService,
    public session: SessionService,
    private router: Router,
    private errorService: ErrorService
  ) { }

  ngOnInit() {
    this.columns = [
      { field: 'breweryName', headerText: 'Brewery', autoFit: true },
      { field: 'orderNumber', headerText: 'Order Number', autoFit: true },
      { field: 'orderDate', valueAccessor: (_, data) => this.formatDate((data as Order).orderDate), headerText: 'Order Date', autoFit: true },
      { field: 'scheduledShipDate', valueAccessor: (_, data) => this.formatDate((data as Order).scheduledShipDate), headerText: 'Ship Date', autoFit: true },
      { field: 'status', headerText: 'Status', autoFit: true },
      { field: 'salesRep', headerText: 'Sales Rep', autoFit: true },
      { headerText: '', template: this.actionTemplate, width: 56, allowSorting: false, allowFiltering: false, textAlign: 'Center' }
    ];

    this.orderService.getOrders().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: orders => {
        this.allOrders = orders ?? [];
        this.orders = this.allOrders.filter(order => order.lines?.some(line =>
          line.selectedLots?.some(selectedLot => selectedLot.lotNumber === this.lot?.lotNumber)));
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  formatDate(value: Date | string | undefined): string {
    return value && moment(value).year() != 0 ? moment(value).format('MM/DD/YYYY') : '';
  }

  onRowSelected(args: RowSelectEventArgs) {
    const order = args?.data as Order;
    if (order) this.openOrder(order);
  }

  openOrder(order: Order): void {
    this.session.order = order;
    this.session.orders = this.allOrders;
    this.router.navigateByUrl(`/hollingberyandson/orders/order-details?orderId=${order.recordId}`);
  }

  trackByOrder(_: number, order: Order) {
    return order.recordId;
  }
}
